import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  deleteAssortmentCode,
  postUserPreferencesSuccess,
  selectAssortmentList,
  selectIsUpdatingPreferences,
  submitAssortmentCode,
} from '@epicuro-next/state/user-state';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { map, take, tap } from 'rxjs/operators';

@Component({
  selector: 'epc-assortment-manager',
  templateUrl: './assortment-manager.component.html',
  styleUrls: ['./assortment-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AssortmentManagerComponent implements OnDestroy {
  public readonly ALREADY_EXISTS_ERROR = 'alreadyExists';
  public readonly ASSORTMENT_CODE_CONTROL_NAME = 'assortmentCode';

  public assortments$ = this.store.pipe(select(selectAssortmentList));

  public isUpdateInProgress$ = this.store.pipe(
    select(selectIsUpdatingPreferences),
  );

  public assortmentForm = this.fb.group({
    [this.ASSORTMENT_CODE_CONTROL_NAME]: [
      '',
      Validators.required,
      this.isCodeAlreadyPresentValidator.bind(this),
    ],
  });

  public get assortmentCodeControl() {
    return this.assortmentForm.get(
      this.ASSORTMENT_CODE_CONTROL_NAME,
    ) as UntypedFormControl;
  }

  public clearOnSuccessSubscription = this.actions$
    .pipe(
      ofType(postUserPreferencesSuccess),
      tap(() => {
        this.assortmentCodeControl.patchValue('');
      }),
    )
    .subscribe();

  public onSubmit() {
    this.store.dispatch(
      submitAssortmentCode({
        assortmentCode: (this.assortmentCodeControl.value as string).trim(),
      }),
    );
  }

  public onDelete(assortmentCodeIndex: number) {
    this.store.dispatch(deleteAssortmentCode({ assortmentCodeIndex }));
  }

  private isCodeAlreadyPresentValidator(
    control: AbstractControl,
  ): ValidationErrors {
    return this.assortments$.pipe(
      take(1),
      map((assortments) => {
        if (assortments?.includes(control.value)) {
          return {
            [this.ALREADY_EXISTS_ERROR]: true,
          };
        }

        return null;
      }),
    );
  }

  public ngOnDestroy() {
    this.clearOnSuccessSubscription.unsubscribe();
  }

  constructor(
    private store: Store,
    private fb: UntypedFormBuilder,
    private actions$: Actions,
  ) {}
}
